import React, { FC, useMemo } from 'react'
import { SensorColumnsDataGrid } from 'src/components/columns/sensor'
import { Sensor, SensorState } from 'src/api'
import { SensorsMapView } from 'src/components/ui'
import { useQueryParamsState } from 'src/hooks/ui'
import { SearchParamsKeys, SensorButtons, SensorFilters, ViewTypes } from 'src/enums'
import { SensorListFilters } from 'src/components/forms/sensor'
import { SensorMapFilters } from 'src/components/forms/sensor/sensorMapFilters'
import { CommonSensorList } from './commonSensorsList'
import { ContractorsDataLoader } from './contractorsDataLoader'
import { SensorContextMenu, SensorMenuItemKey, useSensorContextMenu, createActionsColumn } from './sensorContextMenu'

const getColumns = ({ handleOpenMenu }: { handleOpenMenu: (e: any, row: any) => void }) => [
  SensorColumnsDataGrid.Id(),
  SensorColumnsDataGrid.Model,
  SensorColumnsDataGrid.IMEI,
  SensorColumnsDataGrid.Label,
  SensorColumnsDataGrid.FirmwareVersion,
  SensorColumnsDataGrid.AvailableInThingspace,
  SensorColumnsDataGrid.ActivationStatus,
  SensorColumnsDataGrid.LastPing,
  SensorColumnsDataGrid.SignalStrength,
  SensorColumnsDataGrid.BatteryLevel,
  SensorColumnsDataGrid.DateAdded,
  SensorColumnsDataGrid.LastActiveContractor,
  SensorColumnsDataGrid.Project,
  SensorColumnsDataGrid.State,
  SensorColumnsDataGrid.ServicePlan,
  SensorColumnsDataGrid.EnabledBluetooth,
  SensorColumnsDataGrid.StateUpdatedAt,
  SensorColumnsDataGrid.StateAuthor,
  SensorColumnsDataGrid.HasConfiguration,
  SensorColumnsDataGrid.Period,
  SensorColumnsDataGrid.OrderDate,
  SensorColumnsDataGrid.OrderNumber,
  SensorColumnsDataGrid.OrderPart,
  createActionsColumn(handleOpenMenu)
]

interface AllSensorListProps {
  isLoading: boolean
  count: number
  data: Sensor[]
  states: SensorState[]
  refetchSensorList: () => void
}

export const AllSensorList: FC<AllSensorListProps> = ({ data, isLoading, count, states, refetchSensorList }) => {
  const { currentRow, menuAnchorEl, handleOpenMenu, handleCloseMenu } = useSensorContextMenu()
  const queryParams = useQueryParamsState()
  const viewType = queryParams[SearchParamsKeys.ViewType] || ViewTypes.List

  const dataWithState = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        state: states.find((e) => e.id === row.stateId)
      })),
    [data, states]
  )

  return viewType === ViewTypes.List ? (
    <>
      <CommonSensorList
        columns={getColumns({ handleOpenMenu })}
        count={count}
        data={dataWithState}
        isLoading={isLoading}
        filters={
          <SensorListFilters
            activeFilters={[
              SensorFilters.LowBattery,
              SensorFilters.WeakSignal,
              SensorFilters.LastPingMoreThanHour,
              SensorFilters.SyncPeriod,
              SensorFilters.ReadingsAge,
              SensorFilters.CurrentSensorConfiguration,
              SensorFilters.BleStatus,
              SensorFilters.FirmwareVersion,
              SensorFilters.AvailableInThingspace,
              SensorFilters.ActivationStatus,
              SensorFilters.OrderNumber,
              SensorFilters.OrderDate
            ]}
          />
        }
        buttons={[SensorButtons.QualityControl, SensorButtons.GetHistoricalData]}
        checkboxSelection
      />
      <SensorContextMenu
        menuItems={[SensorMenuItemKey.MarkAsLost, SensorMenuItemKey.MakeAvailable]}
        currentRow={currentRow}
        menuAnchorEl={menuAnchorEl}
        onOpenMenu={handleOpenMenu}
        onCloseMenu={handleCloseMenu}
        states={states}
        refetchSensorList={refetchSensorList}
      />
    </>
  ) : (
    <SensorsMapView
      data={dataWithState}
      isLoading={isLoading}
      filters={
        <ContractorsDataLoader>
          <SensorMapFilters
            activeFilters={[
              SensorFilters.Contractor,
              SensorFilters.LowBattery,
              SensorFilters.WeakSignal,
              SensorFilters.LastPingMoreThanHour,
              SensorFilters.SyncPeriod,
              SensorFilters.ReadingsAge,
              SensorFilters.LocationType,
              SensorFilters.OrderNumber,
              SensorFilters.OrderDate
            ]}
          />
        </ContractorsDataLoader>
      }
    />
  )
}
